import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash, FileText, Eye, Pencil, ShareNetwork, WhatsappLogo, Copy, FilePdf, Upload, X, CookingPot, MagnifyingGlass, Camera, FileXls, Gear, Scales, Buildings, ArrowsClockwise, SortAscending } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useLanguage } from "@/contexts/LanguageContext";
import RecipeImportAI from "@/components/RecipeImportAI";
import ExportTemplateConfig from "@/components/ExportTemplateConfig";
import ExportSelectorDialog from "@/components/ExportSelectorDialog";
import { resolveImageUrl, formatCurrency, calcBakerPercent, NON_METRIC_UNITS } from "@/pages/sheetUtils";
import { IngredientCombobox } from "@/components/IngredientCombobox";

export default function InlineIngredientEditor(props) {
  const { t } = useLanguage();
  const { editIngForm, editingIngIndex, handleSaveEditIngredient, ingredients, setEditIngForm, setEditingIngIndex } = props;
  return (
    <>
          <Dialog open={editingIngIndex !== null} onOpenChange={(o) => { if (!o) { setEditingIngIndex(null); setEditIngForm(null); } }}>
            <DialogContent className="max-w-md" data-testid="edit-ingredient-dialog">
              <DialogHeader>
                <DialogTitle className="text-lg font-bold">{t("ts_edit_ingredient")}</DialogTitle>
                <DialogDescription className="text-xs text-zinc-400">Altera apenas esta ficha técnica — o cadastro mestre do insumo não é afetado.</DialogDescription>
              </DialogHeader>
              {editIngForm && (
                <div className="space-y-4">
                  {!editIngForm.is_sub_recipe && (
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Insumo</Label>
                      <div className="mt-1.5">
                        <IngredientCombobox
                          items={ingredients.map(i => ({ id: i.id, label: `${i.name} (${formatCurrency(i.cost_per_unit)}/${i.unit})`, estimated: i.is_estimated }))}
                          value={editIngForm.ingredient_id}
                          onSelect={(v) => {
                            const m = ingredients.find(i => i.id === v);
                            setEditIngForm({ ...editIngForm, ingredient_id: v, ingredient_name: m ? m.name : editIngForm.ingredient_name, unit: m ? m.unit : editIngForm.unit });
                          }}
                          placeholder="Selecione o insumo"
                          testId="edit-ing-select"
                        />
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <input type="checkbox" id="edit-ing-qb" checked={editIngForm.is_qb} onChange={(e) => setEditIngForm({ ...editIngForm, is_qb: e.target.checked })} className="h-4 w-4" data-testid="edit-ing-qb" />
                    <Label htmlFor="edit-ing-qb" className="text-sm text-zinc-700">QB (Quanto Baste) — sem quantidade fixa, custo zerado</Label>
                  </div>
                  {!editIngForm.is_qb && (
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Peso Bruto</Label>
                        <Input type="number" step="any" min="0" value={editIngForm.quantidade_bruta} onChange={(e) => setEditIngForm({ ...editIngForm, quantidade_bruta: e.target.value })} className="mt-1.5" data-testid="edit-ing-bruta" />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">U.M.</Label>
                        <Select value={editIngForm.unit} onValueChange={(v) => setEditIngForm({ ...editIngForm, unit: v })}>
                          <SelectTrigger className="mt-1.5" data-testid="edit-ing-unit"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {["g", "kg", "ml", "l", "unidades"].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Fator (FC/IC)</Label>
                        <Input type="number" step="any" min="0" value={editIngForm.fator_coccao} onChange={(e) => setEditIngForm({ ...editIngForm, fator_coccao: e.target.value })} className="mt-1.5" data-testid="edit-ing-fc" />
                      </div>
                    </div>
                  )}
                  {!editIngForm.is_qb && editIngForm.quantidade_bruta && (
                    <p className="text-xs text-zinc-400">Peso líquido calculado: <span className="font-mono text-zinc-700">{((parseFloat(editIngForm.quantidade_bruta) || 0) * (parseFloat(editIngForm.fator_coccao) || 1)).toFixed(4)} {editIngForm.unit}</span></p>
                  )}
                  <div className="flex gap-2 pt-1">
                    <Button type="button" variant="outline" className="flex-1" onClick={() => { setEditingIngIndex(null); setEditIngForm(null); }} data-testid="edit-ing-cancel">{t("ts_cancel")}</Button>
                    <Button type="button" className="flex-1 bg-zinc-950 text-white hover:bg-zinc-800" onClick={handleSaveEditIngredient} data-testid="edit-ing-save">{t("ts_save_changes")}</Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
    </>
  );
}
